import { cardColorFor } from "../../../_lib/cardColor";

/*
 * 상품권 카드 아트 8종 — 손으로 그린 SVG (`docs/design/simple-skin/README.md` §6).
 *
 * 🔴 **모티프는 흰색 계열만 씁니다.** 카드 색이 무엇이든 그대로 얹힙니다 — 색 8종을 바꿔도
 *    브랜드색이 바뀌어도 아트를 다시 그릴 일이 없습니다. ⚠️ 예외 하나: **영화의 필름 구멍은
 *    카드 색으로 뚫습니다**(흰 바탕에 흰 구멍이면 보이지 않습니다 — 결정 기록 §7).
 *
 * 🔴 **모서리는 SVG 가 아니라 감싸개가 깎습니다** (`--tnf-radius-art` · `-art-big`). 시안은
 *    `<clipPath rx="12">` 로 숫자를 박았는데, 그러면 `round` 설정을 따라가지 않습니다. 감싸개의
 *    `border-radius` + `overflow: hidden` 이면 토큰 하나로 움직이고 clipPath id 가 겹칠 일도 없습니다.
 *
 * 🔑 **분류는 색에서 고릅니다.** API 에 분류 필드가 없어 `cardColorFor()`(이름·발행처 키워드 →
 *    8색, 없으면 결정적 해시)가 이미 색을 정하고, 8색과 8모티프가 1:1 이라 색이 곧 분류입니다.
 *
 * ⚠️ **진짜 답은 발행처 카드 아트입니다**(README §6) — 제휴·라이선스가 풀리면 이 자리를 이미지로.
 *
 * 사용: `<CardArt name={p.name} brand={p.brandName} width={48} />` · 랜딩은 `height` · `big`.
 */

const W92 = "rgb(255 255 255 / 92%)";
const W58 = "rgb(255 255 255 / 58%)";
const W26 = "rgb(255 255 255 / 26%)";

/** 100×100 좌표의 모티프. `color` 는 영화 구멍처럼 카드 색이 필요한 자리에만 씁니다. */
const MOTIFS: Record<string, (color: string) => React.ReactNode> = {
  "#5b3fc4": () => (
    <>
      <circle cx="34" cy="70" r="13" fill={W92} />
      <circle cx="72" cy="62" r="13" fill={W58} />
      <path d="M45 70V30l38-8v40" stroke={W92} strokeWidth="7" fill="none" strokeLinecap="round" strokeLinejoin="round" />
    </>
  ),
  "#17694b": () => (
    <>
      <path d="M50 32c-9-7-21-9-32-7v46c11-2 23 0 32 7z" fill={W92} />
      <path d="M50 32c9-7 21-9 32-7v46c-11-2-23 0-32 7z" fill={W58} />
      <path d="M50 32v46" stroke={W26} strokeWidth="4" strokeLinecap="round" />
    </>
  ),
  "#1f56b8": () => (
    <>
      <path d="M32 40h36l-5 42a6 6 0 0 1-6 5H43a6 6 0 0 1-6-5z" fill={W92} />
      <rect x="28" y="30" width="44" height="11" rx="5.5" fill={W58} />
      <path d="M58 30 66 14" stroke={W58} strokeWidth="6" strokeLinecap="round" />
    </>
  ),
  "#b0511c": () => (
    <>
      <circle cx="44" cy="56" r="22" fill={W92} />
      <circle cx="44" cy="56" r="11" fill={W26} />
      <path d="M74 26v56" stroke={W58} strokeWidth="7" strokeLinecap="round" />
      <path d="M74 26c8 0 8 22 0 22" stroke={W58} strokeWidth="7" fill="none" strokeLinecap="round" />
    </>
  ),
  "#9c2a66": () => (
    <>
      <path d="M22 40 50 26l28 14v34L50 88 22 74z" fill={W92} />
      <path d="M22 40 50 54l28-14" stroke={W26} strokeWidth="5" fill="none" strokeLinejoin="round" />
      <path d="M50 54v34" stroke={W26} strokeWidth="5" strokeLinecap="round" />
    </>
  ),
  "#0b6b76": () => (
    <>
      <path d="M26 30a6 6 0 0 1 6-6h22a6 6 0 0 1 6 6v58H26z" fill={W92} />
      <rect x="34" y="34" width="18" height="14" rx="3" fill={W26} />
      <path d="M60 46h10a6 6 0 0 1 6 6v20a5 5 0 0 0 10 0V44" stroke={W58} strokeWidth="6" fill="none" strokeLinecap="round" strokeLinejoin="round" />
    </>
  ),
  "#8a2b3d": (color) => (
    <>
      <rect x="20" y="28" width="60" height="56" rx="8" fill={W92} />
      {[34, 52, 70].map((y) => (
        <g key={y}>
          <rect x="25" y={y} width="9" height="9" rx="2.5" fill={color} />
          <rect x="66" y={y} width="9" height="9" rx="2.5" fill={color} />
        </g>
      ))}
      <rect x="39" y="36" width="22" height="40" rx="4" fill={color} opacity="0.55" />
    </>
  ),
  "#2f3a4c": () => (
    <>
      <path d="M26 40h48l-4 46a6 6 0 0 1-6 5H36a6 6 0 0 1-6-5z" fill={W92} />
      <path d="M38 40V30a12 12 0 0 1 24 0v10" stroke={W58} strokeWidth="6" fill="none" strokeLinecap="round" />
    </>
  ),
};

export function CardArt({
  name,
  brand,
  width,
  height = width,
  big = false,
  className = "",
}: {
  name: string;
  brand?: string | null;
  width: number;
  height?: number;
  /** 랜딩의 큰 카드 — 모서리가 `art-big` 입니다 */
  big?: boolean;
  className?: string;
}) {
  const color = cardColorFor(name, brand);
  const motif = MOTIFS[color] ?? MOTIFS["#2f3a4c"];
  /* 시안의 비율 그대로 — 사선 면은 가로 58%·14% 지점, 모티프는 짧은 변의 0.78 배. */
  const scale = Math.min(width, height) * 0.0078;

  return (
    <span
      aria-hidden="true"
      className={`block flex-none overflow-hidden ${big ? "rounded-[var(--tnf-radius-art-big)]" : "rounded-[var(--tnf-radius-art)]"} ${className}`}
      style={{ width, height }}
    >
      <svg viewBox={`0 0 ${width} ${height}`} width={width} height={height} className="block">
        <rect width={width} height={height} fill={color} />
        <path d={`M${width * 0.58} 0 H${width} V${height} H${width * 0.14} Z`} fill="rgb(255 255 255 / 7%)" />
        <g transform={`translate(${width / 2} ${height / 2}) scale(${scale}) translate(-50 -50)`}>{motif(color)}</g>
      </svg>
    </span>
  );
}
